import { useCallback } from 'react';
import { ContentType } from '../../../types/contents';
import { ExerciseType } from '@org/model';
import type { StudentContent, StudentExerciseContent } from './use-class-blocks';
import { TextContentDisplay } from '../components/contents/text-content';
import { ImageContentDisplay } from '../components/contents/image-content';
import { VideoContentDisplay } from '../components/contents/video-content';
import { AudioContentDisplay } from '../components/contents/audio-content';
import { DocumentContentDisplay } from '../components/contents/document-content';
import { MultipleChoiceExercise } from '../components/contents/multiple-choice-exercise';
import { WrittenExercise } from '../components/contents/written-exercise';
import { RecordedExercise } from '../components/contents/recorded-exercise';
import { FillInBlankExercise } from '../components/contents/fill-in-blank-exercise';
import { SelectForBlankExercise } from '../components/contents/select-for-blank-exercise';
import { ConversationalExercise } from '../components/contents/conversational-exercise';
import { MatchingExercise } from '../components/contents/matching-exercise';
import { ScrambleExercise } from '../components/contents/scramble-exercise';
import { FileUploadExercise } from '../components/contents/file-upload-exercise';
import type {
  ContentDisplayVariant,
  ExerciseFeedbackMap,
  ExerciseSubmitHandler,
  RenderContentOptions,
  UseContentRendererOptions,
} from './content-renderer-types';

export type {
  ExerciseSubmission,
  ExerciseSubmitHandler,
  UseContentRendererOptions,
} from './content-renderer-types';

export function useContentRenderer(options: UseContentRendererOptions = {}) {
  const { onExerciseSubmit, exerciseFeedback, isReadOnly, viewerStudentId } = options;

  const renderContent = useCallback(
    (content: StudentContent, renderOptions?: RenderContentOptions) => (
      <ContentRenderer
        key={content.id}
        content={content}
        onExerciseSubmit={renderOptions?.readOnly || isReadOnly ? undefined : onExerciseSubmit}
        exerciseFeedback={exerciseFeedback}
        variant={renderOptions?.variant ?? 'card'}
        isReadOnly={renderOptions?.readOnly ?? isReadOnly}
        viewerStudentId={viewerStudentId}
      />
    ),
    [exerciseFeedback, isReadOnly, onExerciseSubmit, viewerStudentId]
  );

  return { renderContent };
}

interface ContentRendererProps {
  content: StudentContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  exerciseFeedback?: ExerciseFeedbackMap;
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
  viewerStudentId?: string;
}

function ContentRenderer({
  content,
  onExerciseSubmit,
  exerciseFeedback,
  variant = 'card',
  isReadOnly = false,
  viewerStudentId,
}: ContentRendererProps) {
  switch (content.type) {
    case ContentType.Text:
      return <TextContentDisplay content={content} variant={variant} />;
    case ContentType.Image:
      return <ImageContentDisplay content={content} variant={variant} />;
    case ContentType.Video:
      return <VideoContentDisplay content={content} variant={variant} />;
    case ContentType.Audio:
      return <AudioContentDisplay content={content} variant={variant} />;
    case ContentType.Document:
      return <DocumentContentDisplay content={content} variant={variant} />;
    case ContentType.Exercise:
      return (
        <ExerciseContentDisplay
          content={content}
          onExerciseSubmit={onExerciseSubmit}
          exerciseFeedback={exerciseFeedback}
          variant={variant}
          isReadOnly={isReadOnly}
          viewerStudentId={viewerStudentId}
        />
      );
    default:
      return null;
  }
}

function ExerciseContentDisplay({
  content,
  onExerciseSubmit,
  exerciseFeedback,
  variant,
  isReadOnly,
  viewerStudentId,
}: {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  exerciseFeedback?: ExerciseFeedbackMap;
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
  viewerStudentId?: string;
}) {
  const feedback = exerciseFeedback?.[content.exerciseId];
  switch (content.exerciseSubtype) {
    case ExerciseType.MultipleChoice:
      return (
        <MultipleChoiceExercise
          content={content}
          onExerciseSubmit={onExerciseSubmit}
          feedback={feedback}
          variant={variant}
          isReadOnly={isReadOnly}
        />
      );
    case ExerciseType.LongAnswer:
    case ExerciseType.ShortAnswer:
      return (
        <WrittenExercise
          content={content}
          onExerciseSubmit={onExerciseSubmit}
          feedback={feedback}
          variant={variant}
          isReadOnly={isReadOnly}
        />
      );
    case ExerciseType.Recorded:
      return (
        <RecordedExercise
          content={content}
          onExerciseSubmit={onExerciseSubmit}
          feedback={feedback}
          variant={variant}
          isReadOnly={isReadOnly}
        />
      );
    case ExerciseType.FillInBlank:
      return (
        <FillInBlankExercise
          content={content}
          onExerciseSubmit={onExerciseSubmit}
          feedback={feedback}
          variant={variant}
          isReadOnly={isReadOnly}
        />
      );
    case ExerciseType.SelectForBlank:
      return (
        <SelectForBlankExercise
          content={content}
          onExerciseSubmit={onExerciseSubmit}
          feedback={feedback}
          variant={variant}
          isReadOnly={isReadOnly}
        />
      );
    case ExerciseType.Conversational:
      return (
        <ConversationalExercise
          content={content}
          feedback={feedback}
          variant={variant}
          isReadOnly={isReadOnly}
          viewerStudentId={viewerStudentId}
        />
      );
    case ExerciseType.Matching:
      return (
        <MatchingExercise
          content={content}
          onExerciseSubmit={onExerciseSubmit}
          feedback={feedback}
          variant={variant}
          isReadOnly={isReadOnly}
        />
      );
    case ExerciseType.Scramble:
      return (
        <ScrambleExercise
          content={content}
          onExerciseSubmit={onExerciseSubmit}
          feedback={feedback}
          variant={variant}
          isReadOnly={isReadOnly}
        />
      );
    case ExerciseType.FileUpload:
      return (
        <FileUploadExercise
          content={content}
          onExerciseSubmit={onExerciseSubmit}
          feedback={feedback}
          variant={variant}
          isReadOnly={isReadOnly}
        />
      );
    default:
      return null;
  }
}
